import * as React from "react";
import { Badge, Button } from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import {
    cn,
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import {
    FormStatus,
    PORTAL_SUPPORTED_FORM_TYPES,
} from "../../../client-forms/types/client-forms.types";
import { IUpcomingSession } from "../../../upcoming-sessions/types/upcoming-sessions.types";
import { useFetchUpcomingSessions } from "../../../upcoming-sessions/hooks/queries/upcoming-sessions.queries";
import VideoIcon from "../../../../components/Icons/Video";
import { useFetchPaymentCards } from "../../../billing/hooks/billing.queries";
import { useFetchUserDetails } from "../../../auth/hooks/auth.queries";
import useTelehealthParticipants from "../../../../hooks/useTelehealthParticipants";
import AlertTriangleIcon from "../../../../components/Icons/AlertTriangle";

export default function SessionsUpcoming() {
    const { isProviderOnCall } = useTelehealthParticipants();

    const { data } = useFetchUpcomingSessions();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { data: cards } = useFetchPaymentCards();
    const { data: loggedInUser } = useFetchUserDetails();
    const displayName =
        loggedInUser?.data?.preferred_name ?? loggedInUser?.data?.first_name;

    const sessionHasEnded = (session: IUpcomingSession) =>
        new Date(session.end_date_time).getTime() < Date.now();

    const navigateToPayment = (id: string) => {
        searchParams.set("sessionHistoryId", id);
        if (cards?.data.length === 0) {
            searchParams.set("payment_method", "new_card");
        }
        navigate(`/payment-page?${searchParams.toString()}`);
    };

    const getJoinSessionPath = (session: IUpcomingSession) => {
        if (
            session.delivery_option !== "remote" ||
            !session.session_history_id ||
            sessionHasEnded(session)
        ) {
            return null;
        }
        const params = new URLSearchParams({
            with: `${session.provider.first_name}_${session.provider.last_name}`,
            service: session.service,
            start_date_time: session.start_date_time,
            display_name: displayName ?? "",
        });
        return `/meeting/${session.session_history_id}/waiting-room?${params.toString()}`;
    };

    const getInPersonLocationText = (session: IUpcomingSession) => {
        const locationParts = [
            session.location?.office_name,
            session.location?.location_name,
        ].filter((value): value is string => Boolean(value?.trim()));

        return locationParts.length > 0 ? locationParts.join(" · ") : null;
    };

    const upcomingSessions = data?.data;

    return (
        <div className="mt-16 rounded-r8 border border-white/70 bg-white/95 p-16 shadow-sm">
            <div className="mb-12">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
                    Upcoming sessions
                </p>
                <p className="text-sm text-muted">
                    Booked by your provider. Complete any outstanding steps
                    before each visit.
                </p>
            </div>
            {upcomingSessions && upcomingSessions.length > 0 ? (
                <div className="space-y-4">
                    {upcomingSessions.map((session) => {
                        const joinPath = getJoinSessionPath(session);
                        const consentCompleted =
                            session.consent?.status === FormStatus.COMPLETED ||
                            session.consent?.status === FormStatus.RECEIVED ||
                            session.consent?.status === FormStatus.PAUSED ||
                            session.consent?.status === FormStatus.CANCELLED ||
                            session.consent?.client_submitted;

                        const consentFormUrl =
                            session.consent?.consent_form_type &&
                            PORTAL_SUPPORTED_FORM_TYPES.has(
                                session.consent.consent_form_type
                            ) &&
                            session.booking_id
                                ? `/forms/consent/${session.booking_id}`
                                : session.consent?.consent_form_link;
                        const needsConsent =
                            Boolean(consentFormUrl) &&
                            !session.consent?.client_submitted &&
                            !consentCompleted;
                        const hasSessionEnded = sessionHasEnded(session);
                        const isInPersonPaid =
                            session.is_paid &&
                            session.delivery_option !== "remote";
                        const inPersonLocationText =
                            getInPersonLocationText(session);

                        let sessionAction: React.ReactNode = null;
                        if (hasSessionEnded) {
                            sessionAction = null;
                        } else if (!session.is_paid) {
                            sessionAction = (
                                <Button
                                    onClick={() =>
                                        navigateToPayment(
                                            session.session_history_id
                                        )
                                    }
                                >
                                    Pay now
                                </Button>
                            );
                        } else if (
                            session.delivery_option === "remote" &&
                            joinPath &&
                            (!session.consent ||
                                !session.consent.consent_form_link ||
                                session.consent.client_submitted ||
                                consentCompleted)
                        ) {
                            sessionAction = (
                                <>
                                    <Button
                                        onClick={() => navigate(joinPath)}
                                        variant="normal"
                                        size="small"
                                        className={cn(
                                            "gap-x-2 bg-primary text-white text-sm font-medium p-8 rounded-r8 border border-[#E1E4EA]"
                                        )}
                                    >
                                        <VideoIcon stroke="#ffffff" />
                                        Join session
                                    </Button>
                                    {isProviderOnCall(
                                        session.session_history_id
                                    ) ? (
                                        <div className="text-black text-xs italic flex items-center gap-x-[2px]">
                                            <span className="size-8 rounded-full bg-primary blink" />
                                            Provider has joined the call
                                        </div>
                                    ) : null}
                                </>
                            );
                        }

                        return (
                            <div
                                key={session.session_history_id}
                                className="rounded-r8 border border-stroke-subtle bg-surface-raised px-12 py-10"
                            >
                                <div className="flex items-start justify-between gap-8">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge
                                            content={
                                                <span className="capitalize">
                                                    {removeEnumUnderscore(
                                                        session.service
                                                    )}
                                                </span>
                                            }
                                            bgColor="#E8F1F4"
                                            textColor="var(--color-primary)"
                                        />
                                        <Badge
                                            content={
                                                <span className="capitalize">
                                                    {removeEnumUnderscore(
                                                        session.delivery_option
                                                    )}
                                                </span>
                                            }
                                            bgColor="#F7FAFC"
                                            textColor="#475467"
                                        />
                                        <Badge
                                            content={
                                                session.is_paid
                                                    ? "Paid"
                                                    : "Unpaid"
                                            }
                                            bgColor={
                                                session.is_paid
                                                    ? "#E8F6EC"
                                                    : "#FFF2F2"
                                            }
                                            textColor={
                                                session.is_paid
                                                    ? "#0E5730"
                                                    : "#8A1C1C"
                                            }
                                        />
                                    </div>
                                    <span className="rounded-r8 bg-surface-subtle px-8 py-4 text-sm font-semibold text-primary">
                                        {showMoneyInAppFormat(session.fee)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center flex-wrap gap-2">
                                    <div>
                                        <p className="flex gap-2 text-sm font-semibold text-black">
                                            <span>
                                                {session.provider.first_name}
                                            </span>
                                            {session.provider.last_name}
                                            {session.provider.credential
                                                ? ` · ${session.provider.credential}`
                                                : ""}
                                        </p>
                                        <p className="text-xs text-muted">
                                            {formatDate(
                                                session.start_date_time,
                                                false,
                                                "MM/dd/yyyy • h:mmaaa"
                                            )}
                                        </p>
                                        {session.consent ? (
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`text-xs font-medium ${
                                                        consentCompleted
                                                            ? "text-primary"
                                                            : "text-danger-strong"
                                                    }`}
                                                >
                                                    {consentCompleted
                                                        ? "Consent form completed"
                                                        : "Consent form not completed"}
                                                </span>
                                            </div>
                                        ) : null}
                                        {needsConsent && (
                                            <div className="mt-4 flex items-center justify-between flex-wrap gap-8 rounded-r8 border border-warning-stroke bg-warning-surface px-4 py-4 max-w-fit">
                                                <div className="flex items-center gap-2">
                                                    <AlertTriangleIcon stroke="var(--color-warning-text)" />
                                                    <p className="text-xs font-medium text-warning-strong">
                                                        Action required: Please
                                                        fill out your consent
                                                        form before your
                                                        session.
                                                        <a
                                                            href={
                                                                consentFormUrl
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="ml-2 shrink-0 text-xs font-semibold text-primary underline underline-offset-2"
                                                        >
                                                            Fill out form
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="relative flex flex-col items-end gap-y-4">
                                        {sessionAction}
                                    </div>
                                </div>

                                {isInPersonPaid && inPersonLocationText ? (
                                    <div className="flex items-center gap-2">
                                        <svg
                                            className="mt-[2px] h-12 w-12 shrink-0 text-primary"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <p className="text-xs text-black">
                                            {inPersonLocationText}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <ListState
                    emptyMessage="No upcoming sessions yet"
                    stateHelperText="Upcoming sessions will appear here once confirmed by your therapist. If you have any questions, please contact your therapist."
                    isError={false}
                />
            )}
        </div>
    );
}
